import { setIn } from "formik";

import {
  POPUP_IMPORT_COLUMNS,
  type PopupImportColumnDef,
} from "./popupImportColumnModel";

/** Synthetic row-level error key when the API gives no mappable field paths. */
export const POPUP_IMPORT_ROW_FALLBACK_ERROR_KEY = "_importRow";

function snakeSegmentToCamel(segment: string): string {
  return segment.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

/** Converts one Formik path segment (camelCase or numeric) to snake_case for API key matching. */
function camelIdentifierToSnake(segment: string): string {
  if (/^\d+$/.test(segment)) return segment;
  return segment
    .replace(/([A-Z])/g, (m) => `_${m.toLowerCase()}`)
    .replace(/^_/, "");
}

function buildColumnSnakeLookup(columns: readonly PopupImportColumnDef[]): {
  suffixSnakeToFormikPath: Record<string, string>;
  fullSnakeToFormikPath: Record<string, string>;
} {
  const suffixSnakeToFormikPath: Record<string, string> = {};
  const fullSnakeToFormikPath: Record<string, string> = {};

  const suffixCounts = new Map<string, number>();
  for (const col of columns) {
    const parts = col.formikPath.split(".");
    const last = parts[parts.length - 1] ?? "";
    const sn = camelIdentifierToSnake(last);
    suffixCounts.set(sn, (suffixCounts.get(sn) ?? 0) + 1);
  }

  for (const col of columns) {
    const parts = col.formikPath.split(".");
    const last = parts[parts.length - 1] ?? "";
    const sn = camelIdentifierToSnake(last);
    if (suffixCounts.get(sn) === 1) {
      suffixSnakeToFormikPath[sn] = col.formikPath;
    }

    const fullKey = parts.map((p) => camelIdentifierToSnake(p)).join("_");
    if (!(fullKey in fullSnakeToFormikPath)) {
      fullSnakeToFormikPath[fullKey] = col.formikPath;
    }
  }

  return { suffixSnakeToFormikPath, fullSnakeToFormikPath };
}

const { suffixSnakeToFormikPath, fullSnakeToFormikPath } =
  buildColumnSnakeLookup(POPUP_IMPORT_COLUMNS);

/**
 * Maps GraphQL `ImportError.field` (often snake_case / API naming) to Formik paths
 * relative to one `popups[i]` row.
 */
export function serverFieldToFormikField(field: string): string {
  const trimmed = field.trim();
  if (!trimmed) return "";

  const exactMap: Record<string, string> = {
    name: "popup.name",
    action: "action",
    popup_id: "popupId",
    popup_group_id: "popupGroupId",
    popup: POPUP_IMPORT_ROW_FALLBACK_ERROR_KEY,
    popup_group: "group",
    verification: "verificationFunnelId",
  };

  if (!trimmed.includes(".")) {
    return (
      exactMap[trimmed] ??
      suffixSnakeToFormikPath[trimmed] ??
      fullSnakeToFormikPath[trimmed] ??
      snakeSegmentToCamel(trimmed)
    );
  }

  const parts = trimmed.split(".");
  const headRaw = parts[0] ?? "";
  const rest = parts.slice(1);

  /** First segment for dotted paths only — never map `popup` to `_importRow`. */
  const dottedHeadMap: Record<string, string> = {
    popup: "popup",
    popup_group: "group",
    popup_images: "popupImages",
  };

  const head = dottedHeadMap[headRaw] ?? snakeSegmentToCamel(headRaw);

  if (head === "verification" && rest.length > 0) {
    const sub = rest.join(".");
    if (sub === "funnel_id" || sub === "funnelId")
      return "verificationFunnelId";
    if (sub === "page_id" || sub === "pageId") return "verificationPageId";
  }

  const tail = rest.map(snakeSegmentToCamel).join(".");
  return tail ? `${head}.${tail}` : head;
}

type ImportErr = { field: string; message: string };

export function mergeServerImportErrorsIntoRowShape(
  errors: ReadonlyArray<ImportErr>
): Record<string, unknown> {
  let acc: Record<string, unknown> = {};
  for (const err of errors) {
    const rel = serverFieldToFormikField(err.field);
    if (!rel) {
      const prev = acc[POPUP_IMPORT_ROW_FALLBACK_ERROR_KEY];
      acc[POPUP_IMPORT_ROW_FALLBACK_ERROR_KEY] =
        typeof prev === "string" ? `${prev}, ${err.message}` : err.message;
      continue;
    }
    if (rel === POPUP_IMPORT_ROW_FALLBACK_ERROR_KEY) {
      const prev = acc[POPUP_IMPORT_ROW_FALLBACK_ERROR_KEY];
      acc[POPUP_IMPORT_ROW_FALLBACK_ERROR_KEY] =
        typeof prev === "string" ? `${prev}, ${err.message}` : err.message;
      continue;
    }
    acc = setIn(acc, rel, err.message) as Record<string, unknown>;
  }
  return acc;
}

export type ImportFailureRow = {
  rowIndex: number;
  errors: ReadonlyArray<{ field: string; message: string }>;
};

/** One line per server error for the form Alert (same wording as the old toast summary). */
export function importFailureLinesFromResults(
  failedResults: readonly ImportFailureRow[]
): string[] {
  const lines: string[] = [];
  for (const r of failedResults) {
    for (const e of r.errors) {
      lines.push(`行${r.rowIndex + 1}: ${e.message}`);
    }
  }
  return lines;
}

/** Lines for row-wide / unmapped server errors only (field-bound messages appear on cells). */
export function importRowLevelFailureLinesFromResults(
  failedResults: readonly ImportFailureRow[]
): string[] {
  const lines: string[] = [];
  for (const r of failedResults) {
    for (const e of r.errors) {
      const rel = serverFieldToFormikField(e.field);
      if (!rel || rel === POPUP_IMPORT_ROW_FALLBACK_ERROR_KEY) {
        lines.push(`行${r.rowIndex + 1}: ${e.message}`);
      }
    }
  }
  return lines;
}

const IMPORT_FAILURE_LINE_ROW_RE = /^行(\d+):/;

/** Drop errors for deleted row; keep alignment for remaining rows. */
export function remapPopupErrorsAfterRowDelete(
  errors: unknown,
  deletedIndex: number
): unknown {
  if (!errors || typeof errors !== "object") return errors;
  const popups = (errors as { popups?: unknown }).popups;
  if (!Array.isArray(popups)) return errors;
  return {
    ...(errors as object),
    popups: popups.filter((_, i) => i !== deletedIndex),
  };
}

/** Drop `行N:` lines for deleted row; renumber 1-based row labels after it. */
export function remapImportFailureLinesAfterRowDelete(
  lines: string[] | undefined,
  deletedIndex: number
): string[] | undefined {
  if (!lines?.length) return lines;
  const deletedRowNum = deletedIndex + 1;
  const next: string[] = [];
  for (const line of lines) {
    const m = line.match(IMPORT_FAILURE_LINE_ROW_RE);
    if (!m) {
      next.push(line);
      continue;
    }
    const rowNum = Number.parseInt(m[1], 10);
    if (rowNum === deletedRowNum) continue;
    if (rowNum > deletedRowNum) {
      next.push(line.replace(IMPORT_FAILURE_LINE_ROW_RE, `行${rowNum - 1}:`));
      continue;
    }
    next.push(line);
  }
  return next.length > 0 ? next : undefined;
}

export function touchLeafPathsForRowErrors(
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void,
  rowPrefix: string,
  errorNode: unknown
): void {
  if (typeof errorNode === "string") {
    setFieldTouched(rowPrefix, true, false);
    return;
  }
  if (!errorNode || typeof errorNode !== "object" || Array.isArray(errorNode)) {
    return;
  }
  for (const key of Object.keys(errorNode as Record<string, unknown>)) {
    if (key === POPUP_IMPORT_ROW_FALLBACK_ERROR_KEY) continue;
    const val = (errorNode as Record<string, unknown>)[key];
    const next = `${rowPrefix}.${key}`;
    touchLeafPathsForRowErrors(setFieldTouched, next, val);
  }
}
